import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";

import { api } from "../api/client";
import type { AccessLevel, Exercise, ItemRights } from "../api/types";
import { attentionSelector, designate } from "../components/attention";
import { t } from "../i18n/i18n";
import { useUi } from "../state/ui";

/** Propriété et partage côté client (comptes nominatifs, docs/17 §3, §8.1, lot 5).
 *
 *  - Le serveur rend, pour chaque dossier de la liste, son propriétaire, le niveau du compte (`read`, `edit`,
 *    `owner`), son partage et s'il est « de l'unité » ; ces champs sont vides en mode hérité, où rien de nouveau ne
 *    s'affiche (`accountsItem`).
 *  - Un dossier en lecture seule montre un bandeau (« Lecture seule : dossier de {nom}… ») ; toute écriture refusée
 *    par le serveur (403 `READ_ONLY`, `OWNER_ONLY`) le désigne, et l'explication est donnée une fois par un avis
 *    (`ReadOnlyBanner`). Les commandes d'écriture des vues sont bloquées (`blocked`) et leur appui désigne aussi le
 *    bandeau (`designateDossierReadOnly`).
 *  - La fenêtre « Partager » est ouverte depuis l'accueil, la barre latérale et la DIREX (`useSharing`). */

/** Cible du bandeau de lecture seule du dossier (désignée à chaque action bloquée). */
export const READ_ONLY_TARGET = "dossier-read-only";
/** Bouton « Partager… » de la barre latérale (désigné par l'avis « dossier privé » de la DIREX). */
export const SHARE_TARGET = "dossier-share";

/** Élément vu en mode comptes (ou de repli) : le serveur a rendu le niveau du compte. */
export function accountsItem(item: { access?: AccessLevel | null } | null | undefined): boolean {
  return item?.access !== undefined && item?.access !== null;
}

/** Niveau du compte sur le dossier ; « owner » en mode hérité (tout est permis, comme avant). */
export function levelOf(ex: Exercise | null | undefined): AccessLevel {
  return ex?.access ?? "owner";
}

export function readOnly(ex: Exercise | null | undefined): boolean {
  return levelOf(ex) === "read";
}

export function isOwner(ex: Exercise | null | undefined): boolean {
  return levelOf(ex) === "owner";
}

/** Texte du bandeau de lecture seule : « Lecture seule : dossier de {nom}. Demandez-lui l'accès en modification si
 *  vous devez y écrire. » */
export function readOnlyText(name: string): string {
  return t("Lecture seule : dossier de {nom}. Demandez-lui l'accès en modification si vous devez y écrire.", { nom: name });
}

/** Nom affiché du propriétaire (« l'unité » pour un dossier de l'unité). */
export function ownerName(ex: Exercise | null | undefined): string {
  return ex?.owner?.name ?? t("l'unité");
}

/** Dossier ouvert à quelqu'un d'autre que son propriétaire : toute l'unité ou des comptes. */
export function isShared(ex: Exercise): boolean {
  return Boolean(ex.shared && (ex.shared.unit !== "none" || ex.shared.members > 0));
}

/** Filtres de l'accueil (§8.3) : tous, les miens, partagés avec moi, de l'unité. */
export type DossierFilter = "all" | "mine" | "shared" | "unit";

export function matchesFilter(ex: Exercise, filter: DossierFilter, me: string | null): boolean {
  if (filter === "all" || !accountsItem(ex)) return true;
  if (filter === "unit") return Boolean(ex.unit_item);
  const mine = ex.owner?.id !== undefined && ex.owner.id === me;
  return filter === "mine" ? mine : !mine && !ex.unit_item;
}

/** Pastilles d'un dossier (§8.3) : « par {nom} » (dossier d'un autre), « Privé » ou « Partagé · {n} » (les siens),
 *  « Unité » (ouvert à toute l'unité), « Lecture seule », « Dossier de l'unité ». Vide en mode hérité. */
export interface AccessPill {
  key: string;
  label: string;
  tone: "neutral" | "accent" | "warn";
}

export function accessPills(ex: Exercise, me: string | null): AccessPill[] {
  if (!accountsItem(ex)) return [];
  if (ex.unit_item) return [{ key: "unit-item", label: t("Dossier de l'unité"), tone: "neutral" }];
  const pills: AccessPill[] = [];
  const mine = ex.owner?.id === me;
  if (!mine && ex.owner) pills.push({ key: "owner", label: t("par {nom}", { nom: ex.owner.name }), tone: "neutral" });
  if (mine && !isShared(ex)) pills.push({ key: "private", label: t("Privé"), tone: "neutral" });
  if (mine && ex.shared && ex.shared.members > 0)
    pills.push({ key: "shared", label: t("Partagé · {n}", { n: ex.shared.members }), tone: "accent" });
  if (ex.shared && ex.shared.unit !== "none") pills.push({ key: "unit", label: t("Unité"), tone: "accent" });
  if (ex.access === "read") pills.push({ key: "read", label: t("Lecture seule"), tone: "warn" });
  return pills;
}

/** Visibilité d'un ODB ou d'un terrain (§3.6) : pastille « Privé », droits du compte. */
export function itemPrivate(item: ItemRights | null | undefined): boolean {
  return item?.visibility === "private";
}

/** Le compte peut modifier l'élément (propriétaire, administrateur, élément de l'unité) ; vrai en mode hérité. */
export function itemEditable(item: ItemRights | null | undefined): boolean {
  return !accountsItem(item) || item?.access === "edit" || item?.access === "owner";
}

/** Le compte peut supprimer l'élément ou en changer la visibilité ; vrai en mode hérité. */
export function itemOwned(item: ItemRights | null | undefined): boolean {
  return !accountsItem(item) || item?.access === "owner";
}

/** Dossier ouvert, tel que la liste le décrit (même cache que la barre latérale et l'accueil). */
export function useOpenDossier(): Exercise | null {
  const exerciseId = useUi((s) => s.exerciseId);
  const { data } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  return data?.find((e) => e.id === exerciseId) ?? null;
}

/** Accès du compte au dossier ouvert (docs/17 §8.1, `useDossierAccess`) : niveau, lecture seule, propriétaire. */
export function useDossierAccess(): {
  exercise: Exercise | null;
  level: AccessLevel;
  readOnly: boolean;
  owner: boolean;
  ownerName: string;
  accounts: boolean;
} {
  const exercise = useOpenDossier();
  return {
    exercise,
    level: levelOf(exercise),
    readOnly: readOnly(exercise),
    owner: isOwner(exercise),
    ownerName: ownerName(exercise),
    accounts: accountsItem(exercise),
  };
}

/** Action bloquée par la lecture seule du dossier : le bandeau vient à l'écran et clignote — celui de la fenêtre ouverte
 *  s'il y en a une (`DossierReadOnlyNote`), sinon celui de la barre latérale. */
export function designateDossierReadOnly(): void {
  const selector = attentionSelector(READ_ONLY_TARGET);
  const all = typeof document?.querySelectorAll === "function" ? [...document.querySelectorAll<HTMLElement>(selector)] : [];
  const inDialog = all.reverse().find((element) => element.closest('[role="dialog"]'));
  designate(inDialog ?? selector);
}

/** Lecture seule du dossier ouvert, tenue par la coque (`useDossierAccessHost`) : lisible hors rendu par les gestes de
 *  la carte et les écritures du dossier (`dossierWrite`), et par les vues sans relire la liste des dossiers. */
export const useDossierLock = create<{ locked: boolean; ownerName: string }>(() => ({ locked: false, ownerName: "" }));

/** Écriture du dossier ouvert refusée d'avance s'il est en lecture seule : le bandeau est désigné (action attendue :
 *  demander l'accès en modification) ; vrai si refusée. */
export function refuseIfDossierLocked(): boolean {
  if (!useDossierLock.getState().locked) return false;
  designateDossierReadOnly();
  return true;
}

/** Commande d'écriture du dossier ouvert (`Button`) : bloquée en lecture seule, son appui désigne le bandeau ; sinon
 *  le blocage propre à la commande (`blocked`, `onBlocked`) s'applique tel quel. */
export function useWriteBlock(blocked = false, onBlocked?: () => void): { blocked: boolean; onBlocked?: () => void } {
  const locked = useDossierLock((s) => s.locked);
  return locked ? { blocked: true, onBlocked: designateDossierReadOnly } : { blocked, onBlocked };
}

/** Lecture seule du dossier ouvert, pour une vue (abonnée : la commande se débloque dès qu'un accès est donné). */
export function useDossierLocked(): boolean {
  return useDossierLock((s) => s.locked);
}

interface SharingState {
  /** Dossier dont la fenêtre « Partager » est ouverte, et son nom. */
  dialogFor: { id: string; name: string } | null;
  /** Lecture seule déjà expliquée par un avis, par dossier (une fois par dossier et par chargement). */
  explained: string[];
  open: (exercise: { id: string; name: string }) => void;
  close: () => void;
  markExplained: (exerciseId: string) => boolean;
}

export const useSharing = create<SharingState>((set, get) => ({
  dialogFor: null,
  explained: [],
  open: (exercise) => set({ dialogFor: { id: exercise.id, name: exercise.name } }),
  close: () => set({ dialogFor: null }),
  /** Rend vrai la première fois pour ce dossier (avis à donner), faux ensuite (le bandeau suffit). */
  markExplained: (exerciseId) => {
    if (get().explained.includes(exerciseId)) return false;
    set((s) => ({ explained: [...s.explained, exerciseId] }));
    return true;
  },
}));
